<script lang="ts" setup>
import {
    faChevronRight,
    faChevronUp,
    faClose,
    faListCheck,
    faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { getModuleByKey } from '@mrbilit/vue-context-provider';
import SupportQuestionItems from './SupportQuestionItems.vue';
import SessionCard from './_components/SessionCard.vue';
import SupportModal from './_components/SupportModal.vue';
import SupportHistory from './_components/SupportHistory.vue';
import { type SupportWorkFlow } from '@providers/support/supportWorkFlow';
import { type Question } from '@providers/support/Question/Question';
import {
    ActivityType,
    InfoBoxTypeMapper,
    SupportSessionStatus,
    WorkflowStates,
} from '@providers/support/support.type';
import { key } from '@providers/support';
import FailureSvg from '@files/after-order/failure.svg?component';
import FileUploader from '@components/util/FileUploader.vue';
import MrInput from '@components/base/mr-input/MrInput.vue';
import FinalActivity from '@components/ui/support/_components/FinalActivity.vue';

const { sessionId, publicKey, source } = defineProps<{
    sessionId?: number;
    publicKey?: string;
    center?: boolean;
    source?: string;
}>();
const emit = defineEmits<{ close: []; error: [] }>();
const support = getModuleByKey(key);
const showOverlay = ref(false);
const analyticsStore = useAnalyticsStore();

const { $error } = useNotify();
const { login } = useAuth();

const currentStep = computed(() => support.currentWorkFlow?.currentStep);
const supportForm = computed(() => currentStep.value?.form || []);

const showConfirmButton = computed(() => {
    if (!currentStep.value) {
        return false;
    }
    if (support.isLoading || support.isDisconnected) {
        return false;
    }
    if (currentStep.value.activityType === 'Other') {
        return true;
    }
    if (currentStep.value.form.length) {
        return true;
    }
    return currentStep.value.questionOptions.some(
        question => question.isSelected && question.isEditable()
    );
});

const navigateNextStep = async (loggedIn?: boolean) => {
    try {
        await support.currentWorkFlow?.next(loggedIn);
    } catch (e) {
        if (e instanceof Error) {
            $error(e?.message || 'مشکلی پیش امده است.');
        }
    }
};
const selectQuestion = async (question: Question) => {
    if (question.needApproval) {
        const { isConfirmed } = await swal({
            title: 'تایید',
            text: 'آیا اطمینان دارید؟',
            icon: 'question',
            cancelButtonText: 'خیر',
            showCancelButton: true,
            confirmButtonText: 'تایید',
        });
        if (!isConfirmed) {
            return;
        }
    }
    // for question items, we check need login before selecting a step
    let loggedIn = false;
    if (question.needLogin) {
        const isLoggedIn = await login();
        if (!isLoggedIn) {
            return;
        }
        loggedIn = true;
    }
    currentStep.value?.selectQuestion(question.key);
    if (!question.isEditable()) {
        navigateNextStep(loggedIn);
    }
};

const confirm = async () => {
    let loggedIn = false;
    // for submitting forms, we check need login in the same step
    if (currentStep.value?.form.length && currentStep.value.needLogin) {
        const isLoggedIn = await login();
        if (!isLoggedIn) {
            return;
        }
        loggedIn = true;
    }
    navigateNextStep(loggedIn);
};

const navigateButton = computed(() => {
    if (support.isDisconnected) {
        return 'close';
    }
    const hasPending = support.sessions.some(
        session =>
            session.status === SupportSessionStatus.Pending ||
            session.status === SupportSessionStatus.UnderReview
    );
    const currentFlowIndex = currentStep.value?.order ?? -1;
    if (!hasPending && currentFlowIndex === 0) {
        return 'close';
    }
    return currentStep.value?.canBackToPreviousActivityAfterExecution
        ? 'back'
        : 'close';
});

const navigate = () => {
    switch (navigateButton.value) {
        case 'back':
            support.currentWorkFlow?.prev();
            break;
        case 'close':
            emit('close');
            break;
    }
};

const startSession = async (session: SupportWorkFlow) => {
    showOverlay.value = false;
    await support.currentWorkFlow?.stop();
    await session.start();
};

const showHistory = computed(() => {
    if (support.isDisconnected) {
        return false;
    }
    return support.sessions.some(
        ({ status }) =>
            status === SupportSessionStatus.Pending ||
            status === SupportSessionStatus.UnderReview ||
            status === SupportSessionStatus.Expired
    );
});

const pendingSessions = computed(() =>
    support.sessions.filter(
        ({ status }) =>
            status === SupportSessionStatus.Pending ||
            status === SupportSessionStatus.UnderReview
    )
);

const hasInProgress = computed(() =>
    support.sessions.some(
        ({ status }) => status === SupportSessionStatus.Active
    )
);
const confirmButtonText = computed(
    () => currentStep.value?.submitButtonText || 'تایید'
);

const isFinal = computed(
    () =>
        support.currentWorkFlow?.currentStep?.activityType ===
        ActivityType.FinalActivity
);

onBeforeMount(() => {
    analyticsStore.trackAll('support-open', { source });
    window.clarity?.('event', 'support-open');
    support.init(sessionId, publicKey);
});

const returnButton = async () => {
    await support.currentWorkFlow?.stop();
    await support.init();
};
</script>

<template>
    <div class="support-flow-container" @click="emit('close')" />
    <SupportModal :title="currentStep?.activityTitle ?? 'پشتیبانی'" :center>
        <template #back-action>
            <mr-button
                :icon-button="
                    navigateButton === 'back' ? faChevronRight : faClose
                "
                size="small"
                variant="transparent"
                @click="navigate()"
            />
        </template>
        <template v-if="support.isLoading">
            <mr-loading />
            <mr-typography
                v-if="
                    support.currentWorkFlow?.state ===
                    WorkflowStates.reconnecting
                "
                align="center"
                weight="700"
            >
                اتصال قطع شد. در حال اتصال مجدد...
            </mr-typography>
        </template>
        <template v-else-if="support.currentWorkFlow?.state === 'closed'">
            <FailureSvg />
            <mr-typography align="center" weight="700">
                در حال حاضر ارتباط با سرور امکان‌پذیر نمی‌باشد.
                <br />
                لطفاً دقایقی بعد مجدداً تلاش کنید.
            </mr-typography>
        </template>
        <template v-else-if="support.currentWorkFlow">
            <template v-if="currentStep?.infoBoxes">
                <MrFlashMessage
                    v-for="{ message, infoBoxType } in currentStep?.infoBoxes"
                    :key="message"
                    :message
                    :type="InfoBoxTypeMapper[infoBoxType]"
                />
            </template>
            <FinalActivity v-if="isFinal" :support>
                <SupportQuestionItems
                    v-if="currentStep?.questionOptions?.length"
                    :questions="currentStep.questionOptions"
                    @select="selectQuestion"
                    @submit="confirm()"
                />
            </FinalActivity>
            <div v-if="currentStep?.welcomeText" class="start-header">
                <mr-typography size="large" weight="700">
                    <mr-typography type="span" size="display4" weight="900">
                        👋
                    </mr-typography>
                    {{ currentStep.welcomeText }}
                </mr-typography>
                <mr-typography size="large" weight="500">
                    دستیار پشتیبانی آماده پاسخگویی و انجام درخواست شماست.
                </mr-typography>
            </div>
            <div
                v-else-if="
                    (currentStep?.activitySubTitle ||
                        currentStep?.activityDescription) &&
                    !isFinal
                "
                class="step-header"
            >
                <mr-typography
                    v-if="currentStep?.activitySubTitle"
                    weight="700"
                    color="var(--gray-900)"
                    align="right"
                    v-html="currentStep?.activitySubTitle"
                />
                <mr-typography
                    v-if="currentStep?.activityDescription"
                    size="small"
                    color="var(--cool-gray-700)"
                    align="right"
                    v-html="currentStep?.activityDescription"
                />
            </div>
            <SupportQuestionItems
                v-if="currentStep?.questionOptions?.length && !isFinal"
                :questions="currentStep.questionOptions"
                @select="selectQuestion"
                @submit="confirm()"
            />
            <template v-for="field in supportForm" :key="field.key">
                <mr-input
                    v-if="field.isInput()"
                    :required="field.isRequired"
                    :invalid="!field.isValid"
                    hide-description
                    :text-area="field.multiline"
                    full-width
                    :label="field.label"
                    :placeholder="field.description"
                    :model-value="field.value"
                    @update:model-value="
                        field.updateValue($event?.toString() ?? null)
                    "
                />
                <FileUploader
                    v-else-if="field.isImageUploader()"
                    :label="field.label"
                    :model-value="field.value"
                    no-file-label
                    @update:model-value="field.updateValue($event)"
                />
                <mr-rating
                    v-else-if="field.isRating()"
                    label="بازخورد پشتیبانی"
                    alignment="center"
                    :compact-label="field.label"
                    :description="field.description"
                    :model-value="field.value ?? 0"
                    @update:model-value="field.updateValue($event)"
                />
            </template>
        </template>
        <template v-else>
            <div v-if="pendingSessions.length" class="sessions">
                <mr-typography weight="700" color="var(--gray-900)">
                    پشتیبانی‌های اخیر
                </mr-typography>
                <SessionCard
                    v-for="session in pendingSessions"
                    :key="session.sessionId"
                    :session
                    @start-session="startSession(session)"
                />
            </div>
            <mr-button
                v-if="!hasInProgress"
                :right-icon="faPlus"
                full-width
                @click="support.addNewSession()"
            >
                درخواست جدید
            </mr-button>
        </template>
        <div
            v-if="showConfirmButton || currentStep?.hasReturnButton"
            class="confirm-spacer"
        />
        <div v-if="showHistory" class="footer-spacer" />
        <template v-if="showOverlay" #overlay>
            <SupportHistory
                :sessions="support.sessions || []"
                @close="showOverlay = false"
                @start-session="startSession"
            />
        </template>
        <template #footer>
            <div
                v-if="showConfirmButton || currentStep?.hasReturnButton"
                class="confirm-wrapper"
            >
                <div class="background-container">
                    <mr-button
                        v-if="currentStep?.hasReturnButton"
                        full-width
                        variant="bordered"
                        @click="returnButton()"
                    >
                        بازگشت
                    </mr-button>
                    <mr-button
                        v-if="showConfirmButton"
                        full-width
                        @click="confirm()"
                    >
                        {{ confirmButtonText }}
                    </mr-button>
                </div>
            </div>
            <div
                v-if="showHistory"
                class="session-footer"
                @click="showOverlay = true"
            >
                <div class="footer-helper">
                    <mr-icon :icon="faListCheck" size="small" />
                    <mr-typography weight="500" color="var(--gray-900)">
                        لیست درخواست‌ها
                    </mr-typography>
                </div>
                <div class="footer-helper">
                    <mr-chips v-if="pendingSessions.length">
                        <mr-typography size="x-small" weight="500">
                            {{ pendingSessions.length }} درخواست در حال پیگیری
                        </mr-typography>
                    </mr-chips>
                    <mr-icon :icon="faChevronUp" size="small" />
                </div>
            </div>
        </template>
    </SupportModal>
</template>

<style lang="scss" scoped>
.support-flow-container {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    left: 0;
    background-color: white;
    z-index: -1;
    @include desktop-only {
        background-color: #0b5fb34d;
        backdrop-filter: blur(10px);
    }
}

.confirm-spacer {
    height: 50px;
    flex-shrink: 0;
}

.footer-spacer {
    height: 50px;
    flex-shrink: 0;
}

.step-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

.start-header {
    display: flex;
    flex-direction: column;
}

.sessions {
    display: flex;
    flex-direction: column;
    background-color: usePalette($p-cool-gray, $p-200);
    padding: 12px;
    gap: 8px;
    border-radius: 12px;
    border: 1px solid usePalette($p-cool-gray, $p-400);
}

.support-overlay {
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 16px;
    height: 100%;
    background-color: white;
    @include shadow();
    @include desktop-only {
        border-radius: 12px;
    }
}

.confirm-wrapper {
    padding-inline: 8px;
    margin-bottom: 8px;
    .background-container {
        display: flex;
        gap: 8px;
        background-color: white;
    }
}

.session-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 16px;
    border: 1px solid usePalette($p-gray, $p-300);
    background-color: usePalette($p-gray, $p-200);
    cursor: pointer;
    .footer-helper {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    @include desktop-only {
        border-radius: 0 0 12px 12px;
    }
}
</style>
